import React, { useState, useCallback } from "react";
import "./MetaLiftForm.scss";
import MetaLiftFormField from "./MetaLiftFormField";
import { Button as ButtonIcon, ButtonType } from "../Components/Button/Button";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework/ButtonFramework";
import { SingleDatePicker } from "../Components/DatePickers";
import { Dropdown, DropdownToggleType } from "../Components/Dropdown/Dropdown";
import { InfoTooltip } from "../Components/InfoTooltip/InfoTooltip";
import CheckBox from "./CheckBox";
import { DateRange } from "../utils/types";
import MetaLiftCampaignCell from "./MetaLiftCampaignCell";
import { MdAdd, MdEdit } from "react-icons/md";
import MetaLiftObjectiveCell, {
  CustomConversionOption,
  CustomEventOption,
} from "./MetaLiftObjectiveCell";
import HoldoutPercentageModal from "./HoldoutPercentageModal";
import PowerCheckModals from "./PowerCheckModals";
import { formatPercent } from "@blisspointmedia/bpm-types/dist/formatUtils";
import TimePicker from "./TimePicker";
import { TestPowerStatus } from "./MetaLiftCreate";
import {
  CellPowerResult,
  getActiveTestType,
  getTestTypeLabels,
  TIMEZONE_OPTIONS,
  TimezoneOption,
} from "./MetaLiftStudyUtils";
import {
  MetaBudgetInfo,
  CampaignCell,
  ObjectiveCell,
} from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";
import { parseISO, isBefore, startOfToday, isEqual } from "date-fns";

interface MetaLiftFormProps {
  testName: string;
  setTestName: (testName: string) => void;
  testDescription: string;
  setTestDescription: (testDescription: string) => void;
  testHypothesis: string;
  setTestHypothesis: (testHypothesis: string) => void;
  plannedActions: string;
  setPlannedActions: (plannedActions: string) => void;
  dateInputs: Partial<DateRange>;
  setDateInputs: (dateInputs: Partial<DateRange>) => void;
  timeZone: TimezoneOption;
  setTimeZone: (timeZone: TimezoneOption) => void;
  startTime: string;
  setStartTime: (startTime: string) => void;
  endTime: string;
  setEndTime: (endTime: string) => void;
  selectedTestOptions: {
    accounts: boolean;
    campaigns: boolean;
    adSets: boolean;
  };
  setSelectedTestOptions: (selectedTestOptions: {
    accounts: boolean;
    campaigns: boolean;
    adSets: boolean;
  }) => void;
  campaignCells: CampaignCell[];
  addCampaignCell: () => void;
  updateCampaignCellName: (cellId: string, newName: string) => void;
  deleteCampaignCell: (cellId: string) => void;
  updateAllCampaignCellHoldoutPercentages: (
    updatedCells: Array<{ id: string; testPercentage: number; controlPercentage: number }>
  ) => void;
  toggleItemSelection: (cellId: string, itemId: string, accountId: string, name: string) => void;
  campaignOptions: MetaBudgetInfo[];
  lockedAccountId: string | null;
  allSelectedItemIds: Set<string>;
  totalBudget: string;
  objectiveCells: ObjectiveCell[];
  addObjectiveCell: () => void;
  updateObjectiveCellName: (cellId: string, newName: string) => void;
  updateObjectiveCellSelections: (
    cellId: string,
    eventNames: string[],
    customConversions: string[]
  ) => void;
  deleteObjectiveCell: (cellId: string) => void;
  customConversions?: CustomConversionOption[];
  customEvents?: CustomEventOption[];
  testPowerStatus: TestPowerStatus;
  neededDailySpend: number;
  historicalDailySpend: number;
  testLengthDays: number;
  cellPowerResults: CellPowerResult[];
  onRunTestPowerCheck: () => Promise<boolean>;
  onCreateTest: () => Promise<string | null>;
  isFormValid: boolean;
  isResultsOutdated: boolean;
  onNavigateToPublished?: () => void;
  isCellToCellSensitivityPowered: boolean;
}

const MetaLiftForm = ({
  testName,
  setTestName,
  testDescription,
  setTestDescription,
  testHypothesis,
  setTestHypothesis,
  plannedActions,
  setPlannedActions,
  dateInputs,
  setDateInputs,
  timeZone,
  setTimeZone,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  selectedTestOptions,
  setSelectedTestOptions,
  campaignCells,
  addCampaignCell,
  updateCampaignCellName,
  deleteCampaignCell,
  updateAllCampaignCellHoldoutPercentages,
  toggleItemSelection,
  campaignOptions,
  lockedAccountId,
  allSelectedItemIds,
  totalBudget,
  objectiveCells,
  addObjectiveCell,
  updateObjectiveCellName,
  updateObjectiveCellSelections,
  deleteObjectiveCell,
  customConversions = [],
  customEvents = [],
  testPowerStatus,
  neededDailySpend,
  historicalDailySpend,
  testLengthDays,
  cellPowerResults,
  onRunTestPowerCheck,
  onCreateTest,
  isFormValid,
  isResultsOutdated,
  onNavigateToPublished,
  isCellToCellSensitivityPowered,
}: MetaLiftFormProps): JSX.Element => {
  const testType = getActiveTestType(selectedTestOptions);
  const labels = getTestTypeLabels(testType);
  const [showHoldoutModal, setShowHoldoutModal] = useState(false);

  const [showRunningModal, setShowRunningModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTestId, setCreatedTestId] = useState("");

  const formatHoldoutDisplay = (): string => {
    if (campaignCells.length === 0) {
      return "No cells";
    }

    const cellDisplays = campaignCells.map(
      (cell, index) =>
        `Cell ${index + 1}: test ${formatPercent(cell.testPercentage, 0)}, control ${formatPercent(
          cell.controlPercentage,
          0
        )}`
    );

    if (campaignCells.length === 1) {
      return cellDisplays[0];
    }

    return cellDisplays.join(" | ");
  };

  const getSelectedTestLevel = (): string => {
    if (selectedTestOptions.accounts) {
      return "Account";
    }
    if (selectedTestOptions.campaigns) {
      return "Campaign";
    }
    if (selectedTestOptions.adSets) {
      return "Ad Set";
    }
    return "";
  };

  const handleReviewAndCreateClick = useCallback(() => {
    setShowReviewModal(true);
  }, []);

  const handleProceedToReview = useCallback(() => {
    setShowResultModal(false);
    setShowReviewModal(true);
  }, []);

  const handleEditTest = useCallback(() => {
    setShowResultModal(false);
    setShowReviewModal(false);
  }, []);

  const [isCreatingTest, setIsCreatingTest] = useState(false);

  const handleCreateTest = useCallback(async () => {
    setIsCreatingTest(true);

    try {
      const testId = await onCreateTest();

      if (testId) {
        setCreatedTestId(testId);
        setShowReviewModal(false);
        setShowSuccessModal(true);
      } else {
        alert("Failed to create test - no ID returned from Meta");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      alert(`Error creating test: ${errorMessage}`);
    } finally {
      setIsCreatingTest(false);
    }
  }, [onCreateTest]);

  const handleOpenCreatedTests = useCallback(() => {
    setShowSuccessModal(false);
    onNavigateToPublished?.();
  }, [onNavigateToPublished]);

  const powerCheckHasRun =
    testPowerStatus === "well_powered" || testPowerStatus === "not_well_powered";
  const isReviewButtonDisabled = !isFormValid;

  return (
    <div className="metaLiftForm">
      <div className="metaLiftWidget">
        <div className="metaLiftWidgetHeader">
          <input
            type="text"
            className="metaLiftWidgetHeaderTitle"
            value={testName}
            onChange={e => setTestName(e.target.value)}
            placeholder="Test Name"
          />
        </div>
        <div className="metaLiftWidgetHeaderSubTitle">
          Include the client name, timing, and what's being tested (e.g.,
          Meta_SummerPromo_Prospects_Jul2025).
        </div>
        <MetaLiftFormField
          title="Test Description"
          placeholder="Briefly explain the purpose or goal of this test."
          value={testDescription}
          onChange={setTestDescription}
        />
        <MetaLiftFormField
          title="Test Hypothesis"
          placeholder="Describe what you expect to learn or prove with this test."
          value={testHypothesis}
          onChange={setTestHypothesis}
        />
        <MetaLiftFormField
          title="Planned Actions"
          placeholder="Enter planned actions..."
          helperText="Outline the steps you'll take before, during, and after the test depending on the results."
          value={plannedActions}
          onChange={setPlannedActions}
        />
      </div>
      <div className="metaLiftWidgetQuestions">
        <div className="metaLiftWidgetQuestionsHeader">
          <div className="metaLiftWidgetQuestionsHeaderTitle">Schedule*</div>
        </div>
        <div className="metaLiftWidgetQuestionsDateAndTime">
          <div className="metaLiftWidgetQuestionsDateAndTimeTitle">
            Select when the test should begin:
          </div>
          <div className="dateDisplay">
            <div className="dateInputWrapper">
              <SingleDatePicker
                date={dateInputs.start || ""}
                onChange={date => {
                  setDateInputs({
                    ...dateInputs,
                    start: date,
                    end: dateInputs.end && date > dateInputs.end ? undefined : dateInputs.end,
                  });
                }}
                isOutsideRange={(date: string) => {
                  if (!date) {
                    return false;
                  }
                  const dateObj = parseISO(date);
                  const today = startOfToday();

                  if (isBefore(dateObj, today) || dateObj.getTime() === today.getTime()) {
                    return true;
                  }

                  return false;
                }}
              />
              <TimePicker value={startTime} onChange={setStartTime} placeholder="Start time" />
              <Dropdown
                type={DropdownToggleType.OUTLINED}
                background="light"
                size="lg"
                options={TIMEZONE_OPTIONS}
                onChange={val => {
                  setTimeZone(val as TimezoneOption);
                }}
                placeholder="Select Time Zone"
                value={timeZone}
              />
            </div>
          </div>
          <div className="metaLiftWidgetQuestionsDateAndTimeTitle">
            Select when the test should end:
          </div>
          <div className="dateDisplay">
            <div className="dateInputWrapper">
              <SingleDatePicker
                date={dateInputs.end || ""}
                onChange={date => {
                  setDateInputs({
                    ...dateInputs,
                    end: date,
                  });
                }}
                isOutsideRange={(date: string) => {
                  if (!date) {
                    return false;
                  }
                  const dateObj = parseISO(date);
                  const today = startOfToday();

                  if (isBefore(dateObj, today) || isEqual(dateObj, today)) {
                    return true;
                  }

                  if (dateInputs.start) {
                    const startDate = parseISO(dateInputs.start);
                    if (isBefore(dateObj, startDate) || isEqual(dateObj, startDate)) {
                      return true;
                    }
                  }

                  return false;
                }}
              />
              <TimePicker value={endTime} onChange={setEndTime} placeholder="End time" />
              <Dropdown
                type={DropdownToggleType.OUTLINED}
                background="light"
                size="lg"
                options={TIMEZONE_OPTIONS}
                onChange={val => {
                  setTimeZone(val as TimezoneOption);
                }}
                placeholder="Select Time Zone"
                value={timeZone}
              />
            </div>
          </div>
        </div>
        <div className="metaLiftWidgetQuestionsHeader">
          <div className="metaLiftWidgetQuestionsHeaderTitle">What do you want to test?*</div>
        </div>
        <div className="metaLiftWidgetQuestionsTestOptions">
          <CheckBox
            type="radio"
            checked={selectedTestOptions.accounts}
            label="Accounts"
            onCheck={() => {
              setSelectedTestOptions({
                accounts: true,
                campaigns: false,
                adSets: false,
              });
            }}
          ></CheckBox>
          <CheckBox
            type="radio"
            checked={selectedTestOptions.campaigns}
            label="Campaigns"
            onCheck={() => {
              setSelectedTestOptions({
                accounts: false,
                campaigns: true,
                adSets: false,
              });
            }}
          ></CheckBox>
          <CheckBox
            type="radio"
            checked={selectedTestOptions.adSets}
            label="Ad Sets"
            onCheck={() => {
              setSelectedTestOptions({
                accounts: false,
                campaigns: false,
                adSets: true,
              });
            }}
          ></CheckBox>
        </div>
      </div>
      <div className="metaLiftWidgetQuestions">
        {" "}
        <div className="metaLiftWidgetQuestionsHeader metaLiftWidgetQuestionsHeader--row">
          <div className="metaLiftWidgetQuestionsHeaderTitle">
            Cell Setup | {labels.headerQuestion}
          </div>
          <InfoTooltip>
            {" "}
            Each test requires at least one cell and can include at most 5 cells. Multiple cells are
            best used to directly test competing strategies against each other, not to evaluate the
            relative performance of different campaigns. For example, if you wanted to evaluate the
            incremental performance of BAU conversion optimized campaigns against revenue optimized
            campaigns, a two-celled test would be the correct option. On the other hand, if you
            wanted to evaluate the incremental performance of prospecting campaigns and retargeting
            campaigns, you should configure two separate one-celled tests.
          </InfoTooltip>
        </div>
      </div>
      {campaignCells.map(cell => (
        <MetaLiftCampaignCell
          key={cell.id}
          cellId={cell.id}
          cellName={cell.name}
          onCellNameChange={newName => updateCampaignCellName(cell.id, newName)}
          onDelete={() => deleteCampaignCell(cell.id)}
          canDelete={campaignCells.length > 1}
          selectedTestOptions={selectedTestOptions}
          campaignOptions={campaignOptions}
          selectedItems={cell.selectedItems}
          onToggleItem={(itemId, accountId, name) =>
            toggleItemSelection(cell.id, itemId, accountId, name)
          }
          lockedAccountId={lockedAccountId}
          allSelectedItemIds={allSelectedItemIds}
        />
      ))}
      <div className="metaLiftCellsHoldoutPercentageContainer">
        <div className="metaLiftCellsHoldoutPercentageHeader">
          <div className="metaLiftCellsHoldoutPercentageHeaderTitle">Holdout Percentage</div>
          <InfoTooltip>
            {" "}
            Holdout Percentages: Default split is 80/20 test vs control. The total values for
            holdout percentage across test and control for all test cells must sum to 100. For
            example, in a two-celled test, Cell A will have 40% test and 10% control and Cell B will
            have 40% test and 10% control by default
          </InfoTooltip>
          <div className="metaLiftCellsHoldoutPercentageHeaderValues">{formatHoldoutDisplay()}</div>
          <ButtonIcon
            className="metaLiftCellsHoldoutPercentageEditButton"
            type={ButtonType.EMPTY}
            variant={ButtonFrameworkVariant.ICON_ONLY}
            onClick={() => setShowHoldoutModal(true)}
            icon={<MdEdit />}
            size="sm"
          ></ButtonIcon>
          <div className="metaLiftCellsHoldoutPercentageAddCellButton">
            <ButtonIcon
              className="metaLiftCellsHoldoutPercentageEditButton"
              type={ButtonType.OUTLINED}
              variant={ButtonFrameworkVariant.TRAILING_ICON}
              onClick={addCampaignCell}
              icon={<MdAdd />}
              size="sm"
              disabled={campaignCells.length >= 5}
            >
              Add Cell
            </ButtonIcon>
          </div>
        </div>
        <div className="metaLiftCellsHoldoutPercentageTotalBudget">
          <div className="metaLiftCellsHoldoutPercentageTotalBudgetTitle">Total Budget</div>
          <div className="metaLiftCellsHoldoutPercentageTotalBudgetValue">{totalBudget}</div>
        </div>
      </div>
      <div className="metaLiftWidgetQuestions">
        <div className="metaLiftWidgetQuestionsHeader metaLiftWidgetQuestionsHeader--column">
          <div className="metaLiftWidgetQuestionsHeaderTitle">
            Objectives and Conversion Sources*
          </div>
          <div className="metaLiftWidgetQuestionsHeaderSubTitle">
            Objective determines the conversion events which the test will evaluate.{" "}
            <span className="metaLiftWidgetQuestionsHeaderSubTitleTooltip">
              <InfoTooltip>
                Objective determines the conversion events which the test will evaluate. For each
                conversion event, you must first select a data source, such as a pixel (which
                includes CAPI), an offline dataset or an app.
              </InfoTooltip>
            </span>
          </div>
        </div>
      </div>
      {objectiveCells.map(cell => (
        <MetaLiftObjectiveCell
          key={cell.id}
          cellId={cell.id}
          cellName={cell.name}
          eventNames={cell.eventNames || []}
          customConversions={cell.customConversions || []}
          onCellNameChange={newName => updateObjectiveCellName(cell.id, newName)}
          onSelectionApply={(eventNames, customConversions) =>
            updateObjectiveCellSelections(cell.id, eventNames, customConversions)
          }
          onDelete={() => deleteObjectiveCell(cell.id)}
          canDelete={objectiveCells.length > 1}
          customConversionsList={customConversions}
          customEventsList={customEvents}
        />
      ))}
      <div className="metaLiftObjectiveCellAddHolder">
        <div className="metaLiftObjectiveCellAddHolderHeader">
          <ButtonIcon
            className="metaLiftObjectiveCellAddHolderAddCellButton"
            type={ButtonType.OUTLINED}
            variant={ButtonFrameworkVariant.TRAILING_ICON}
            onClick={addObjectiveCell}
            icon={<MdAdd />}
            size="sm"
          >
            Add Objective
          </ButtonIcon>
        </div>
      </div>
      <HoldoutPercentageModal
        show={showHoldoutModal}
        onHide={() => setShowHoldoutModal(false)}
        campaignCells={campaignCells}
        onUpdateAll={updateAllCampaignCellHoldoutPercentages}
      />
      <PowerCheckModals
        showRunningModal={showRunningModal}
        showResultModal={showResultModal}
        showReviewModal={showReviewModal}
        showSuccessModal={showSuccessModal}
        onCloseRunningModal={() => setShowRunningModal(false)}
        onCloseResultModal={() => setShowResultModal(false)}
        onCloseReviewModal={() => setShowReviewModal(false)}
        onCloseSuccessModal={() => setShowSuccessModal(false)}
        testPowerStatus={testPowerStatus}
        neededDailySpend={neededDailySpend}
        historicalDailySpend={historicalDailySpend}
        testLengthDays={testLengthDays}
        cellPowerResults={cellPowerResults}
        testName={testName}
        testDescription={testDescription}
        testHypothesis={testHypothesis}
        plannedActions={plannedActions}
        selectedTestLevel={getSelectedTestLevel()}
        totalBudget={totalBudget}
        startDate={dateInputs.start || ""}
        startTime={startTime}
        endDate={dateInputs.end || ""}
        endTime={endTime}
        campaignCells={campaignCells}
        objectiveCells={objectiveCells}
        onReviewAndCreate={handleProceedToReview}
        onEditTest={handleEditTest}
        onCreateTest={handleCreateTest}
        onOpenCreatedTests={handleOpenCreatedTests}
        createdTestId={createdTestId}
        isCreating={isCreatingTest}
        isCellToCellSensitivityPowered={isCellToCellSensitivityPowered}
      />
      <div className="metaLiftWidgetFooter">
        <div className="metaLiftWidgetFooterButtonHolder">
          <div className="metaLiftWidgetFooterDeleteButton"></div>
          <div className="metaLiftWidgetFooterOtherButtons">
            <ButtonIcon
              type={ButtonType.FILLED}
              variant={ButtonFrameworkVariant.LEADING_ICON}
              onClick={powerCheckHasRun ? handleReviewAndCreateClick : onRunTestPowerCheck}
              size="sm"
              disabled={isReviewButtonDisabled}
            >
              {powerCheckHasRun ? "Review and Create Test" : "Run Power Check"}
            </ButtonIcon>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaLiftForm;
